import type { CostDetail, CostSlice } from './api';
import type { CostBar } from './cost-bar';
import { Format } from './format';
import { SliceMetrics } from './slice-metrics';

/**
 * O detalhe de um cliente ou workflow no formato que a tela desenha.
 *
 * A tela só compõe: quem decide o rótulo de cada recorte, o que é dia sem gasto
 * e como o dinheiro vira barra é esta peça.
 */
export class CostDetailView {
  private static readonly ACTION_LABELS: Record<string, string> = {
    generation: 'Geração da resposta',
    guard: 'Guarda de conduta',
    groundedness: 'Juiz de fidelidade',
    reprompt: 'Segunda tentativa',
  };

  /** O total responde "quanto pesou"; o custo por chamada responde "quanto custa
   *  cada vez que isso roda" — é ele que compara um passo com o outro sem o
   *  volume no meio. */
  private static perCall(slice: CostSlice): string {
    if (slice.calls <= 0) return '—';
    return `${Format.brl(slice.brl / slice.calls)} por chamada`;
  }

  private constructor(private readonly detail: CostDetail) {}

  static of(detail: CostDetail): CostDetailView {
    return new CostDetailView(detail);
  }

  get name(): string {
    return this.detail.identity.name;
  }

  get subtitle(): string | null {
    return this.detail.identity.subtitle;
  }

  get isClient(): boolean {
    return this.detail.identity.scope === 'client';
  }

  get total(): CostSlice {
    return this.detail.total;
  }

  get periodLabel(): string {
    const { from, to } = this.detail.period;
    if (from === to) return CostDetailView.dayLabel(from);
    return `${CostDetailView.dayLabel(from)} a ${CostDetailView.dayLabel(to)}`;
  }

  get averageLabel(): string {
    if (this.detail.total.runs <= 0) return '—';
    return Format.brl(this.detail.total.brl / this.detail.total.runs);
  }

  get cacheHitLabel(): string {
    return Format.percent(this.detail.total.tokens.cached, this.detail.total.tokens.prompt);
  }

  get reasoningLabel(): string {
    return Format.percent(this.detail.total.tokens.reasoning, this.detail.total.tokens.completion);
  }

  get dailyAverageLabel(): string {
    if (this.detail.byDay.length === 0) return '—';
    return Format.brl(this.detail.total.brl / this.detail.byDay.length);
  }

  dayBars(): CostBar[] {
    return this.detail.byDay.map((day) => ({
      key: day.day,
      label: CostDetailView.dayLabel(day.day),
      sub: `${Format.count(day.runs)} rodada(s) · ${Format.count(day.calls)} chamada(s) · ${CostDetailView.perCall(day)}`,
      metrics: new SliceMetrics(day).all(),
      ...CostDetailView.money(day.brl),
    }));
  }

  actionBars(): CostBar[] {
    return this.detail.byAction.map((action) => ({
      key: action.action,
      label: CostDetailView.ACTION_LABELS[action.action] ?? action.action,
      sub: `${Format.count(action.calls)} chamada(s) · ${CostDetailView.perCall(action)}`,
      metrics: new SliceMetrics(action).all(),
      ...CostDetailView.money(action.brl),
    }));
  }

  /**
   * Um workflow tem mais de uma ida ao modelo, e elas não custam igual: no
   * moovery o passo que classifica a prioridade responde por uma fatia que a
   * quebra por ação não separa, porque ali ele é só mais uma "geração".
   */
  stepBars(): CostBar[] {
    return this.detail.byStep.map((step) => ({
      key: step.step,
      label: step.step,
      sub: `${Format.count(step.calls)} chamada(s) · ${CostDetailView.perCall(step)}`,
      metrics: new SliceMetrics(step).all(),
      ...CostDetailView.money(step.brl),
    }));
  }

  modelBars(): CostBar[] {
    return this.detail.byModel.map((model) => ({
      key: `${model.provider}|${model.model}`,
      label: model.model ?? '(desconhecido)',
      sub: `${model.provider ?? 'sem provedor'} · ${CostDetailView.perCall(model)}${model.priced ? '' : ' · sem tabela de preço'}`,
      metrics: new SliceMetrics(model).all(),
      ...CostDetailView.money(model.brl),
    }));
  }

  /** Só faz sentido na tela do cliente: no workflow há um só, que é ele mesmo. */
  workflowBars(): CostBar[] {
    if (!this.isClient) return [];
    return this.detail.byWorkflow.map((workflow) => ({
      key: workflow.id,
      label: workflow.name,
      sub: `${Format.count(workflow.runs)} rodada(s) · ${Format.count(workflow.calls)} chamada(s) · ${CostDetailView.perCall(workflow)}`,
      metrics: new SliceMetrics(workflow).all(),
      ...CostDetailView.money(workflow.brl),
    }));
  }

  /**
   * As rodadas de um dia custam todas um centavo, então o dinheiro não separa
   * uma da outra: quem separa é o tamanho da entrada. A barra mede tokens, e o
   * custo vai na linha de apoio, onde ainda é útil e não finge ordenar nada.
   */
  runBars(): CostBar[] {
    return this.detail.costliestRuns.map((run) => ({
      key: run.executionId,
      label: CostDetailView.instantLabel(run.startedAt),
      sub: `${Format.count(run.calls)} chamada(s) · ${Format.brl(run.brl)} · ${run.executionId.slice(
        0,
        8,
      )}`,
      value: `${Format.count(run.tokens.prompt)} tok`,
      metrics: [],
      weight: run.tokens.prompt,
      share: false,
    }));
  }

  private static money(brl: number): { value: string; weight: number; share: boolean } {
    return { value: Format.brl(brl), weight: brl, share: true };
  }

  private static dayLabel(day: string): string {
    const [ano, mes, dia] = day.split('-');
    return `${dia}/${mes}/${ano.slice(2)}`;
  }

  private static instantLabel(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Fortaleza',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
